import React, { useState, useEffect } from 'react';
import {
    Box, TextField, Button, Typography, Alert,
    CircularProgress, Paper, Divider, Chip,
    useTheme
} from '@mui/material';
import {
    Key as KeyIcon,
    Logout as LogoutIcon,
    CheckCircle as CheckIcon,
    Email as EmailIcon,
    ArrowForward as ArrowIcon,
    WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useDesignTheme } from '../../design-system/providers/ThemeProvider';
import { ReactComponent as MensureLogo } from '../../assets/logo.svg';
import api from '../../services/api';

const ActivationPage = () => {
    const theme = useTheme();
    const { primaryGradient } = useDesignTheme();
    const [tokenCode, setTokenCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(null);
    const navigate = useNavigate();
    const { logout, user, updateUser } = useAuth();
    const [contactInfo, setContactInfo] = useState({ whatsapp: '', email: '' });

    // Buscar informações de contato
    useEffect(() => {
        const fetchContact = async () => {
            try {
                const response = await api.get('/system-config/public/contact');
                if (response.data.success) {
                    setContactInfo(response.data.data);
                }
            } catch (err) {
                setContactInfo({ whatsapp: '5511980464551', email: 'contato@mensure.com.br' });
            }
        };
        fetchContact();
    }, []);

    // Redirecionar se já está ativado
    useEffect(() => {
        if (!user) return;
        const BYPASS_ROLES = ['admin', 'mensure', 'curator', 'curador'];
        const isBypass = BYPASS_ROLES.includes(user.role?.toLowerCase());
        if (isBypass || user.activation?.isActivated) {
            navigate('/onboarding', { replace: true });
        }
    }, [user, navigate]);

    // Formata token automaticamente: XXXX-XXXX-XXXX-XXXX
    const handleTokenChange = (e) => {
        const raw = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        const groups = raw.match(/.{1,4}/g) || [];
        setTokenCode(groups.join('-').slice(0, 19));
    };

    const handleActivate = async () => {
        if (!tokenCode.trim()) { setError('Insira o código de ativação'); return; }
        setLoading(true);
        setError('');
        try {
            const response = await api.post('/auth/activate', { tokenCode: tokenCode.trim() });
            if (response.data.success) {
                setSuccess(response.data);
                updateUser({
                    ...user,
                    activation: {
                        ...(user?.activation || {}),
                        isActivated: true,
                        plan: response.data.data?.plan
                    }
                });
                setTimeout(() => navigate('/onboarding'), 2000);
            }
        } catch (err) {
            const msg = err.response?.data?.message || '';
            const lowerMsg = msg.toLowerCase();
            if (err.response?.status === 400 && (lowerMsg.includes('ativad') || lowerMsg.includes('already'))) {
                updateUser({ ...user, activation: { ...(user?.activation || {}), isActivated: true } });
                navigate('/onboarding', { replace: true });
                return;
            }
            setError(msg || 'Token inválido ou já utilizado');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => { logout(); navigate('/login'); };

    const isDark = theme.palette.mode === 'dark';

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100%',
                backgroundColor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: { xs: 1.5, sm: 2 },
                boxSizing: 'border-box',
                overflowX: 'hidden'
            }}
        >
            <Paper
                elevation={2}
                sx={{
                    padding: { xs: 2, sm: 3 },
                    width: '100%',
                    maxWidth: { xs: '100%', sm: 400 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    boxSizing: 'border-box',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                }}
            >
                {/* Logo */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <MensureLogo style={{
                        height: 36,
                        width: 'auto',
                        fill: theme.palette.primary.main,
                        color: theme.palette.primary.main
                    }} />
                </Box>

                {success ? (
                    /* ── Estado de Sucesso ── */
                    <Box textAlign="center" py={2} width="100%">
                        <Box sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            bgcolor: 'success.light',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                            opacity: 0.9,
                        }}>
                            <CheckIcon sx={{ fontSize: 30, color: 'success.main' }} />
                        </Box>
                        <Typography variant="h6" fontWeight={700} color="success.main" mb={0.5}>
                            Conta Ativada!
                        </Typography>
                        <Chip
                            label={`Plano ${success.data?.plan?.name || 'Ativo'}`}
                            color="success" size="small" sx={{ mb: 1.5 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            Redirecionando para configurar sua marca...
                        </Typography>
                        <CircularProgress size={18} sx={{ mt: 2, color: 'success.main' }} />
                    </Box>
                ) : (
                    /* ── Formulário ── */
                    <Box sx={{ width: '100%' }}>
                        <Typography
                            component="h1"
                            variant="h5"
                            sx={{
                                mb: 0.5,
                                fontWeight: 600,
                                color: 'text.primary',
                                textAlign: 'center'
                            }}
                        >
                            Ativar Conta
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
                            Insira o token enviado pela equipe Mensure
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
                                {error}
                            </Alert>
                        )}

                        {/* Campo do token */}
                        <Box mb={1}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary"
                                sx={{ mb: 0.5, display: 'block', letterSpacing: 0.3 }}>
                                CÓDIGO DE ATIVAÇÃO
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="XXXX-XXXX-XXXX-XXXX"
                                value={tokenCode}
                                onChange={handleTokenChange}
                                onKeyDown={e => e.key === 'Enter' && handleActivate()}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                    style: {
                                        fontFamily: 'monospace',
                                        fontSize: '1.05rem',
                                        letterSpacing: '0.12em',
                                        textAlign: 'center',
                                        fontWeight: 600,
                                    },
                                    maxLength: 19,
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 1.5,
                                        bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'primary.main',
                                            borderWidth: 2,
                                        }
                                    },
                                    '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
                                    '& .MuiOutlinedInput-notchedOutline': { top: 0 },
                                }}
                            />
                        </Box>

                        {/* Progresso visual do token */}
                        <Box sx={{ display: 'flex', gap: 0.5, mb: 3 }}>
                            {[0, 1, 2, 3].map(i => {
                                const filled = tokenCode.split('-').filter((_, idx) => idx === i)[0]?.length === 4;
                                return (
                                    <Box key={i} sx={{
                                        flex: 1, height: 3, borderRadius: 2,
                                        bgcolor: filled ? 'primary.main' : 'action.selected',
                                        transition: 'background-color 0.2s',
                                    }} />
                                );
                            })}
                        </Box>

                        {/* Botão principal */}
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleActivate}
                            disabled={loading || tokenCode.replace(/-/g, '').length < 16}
                            endIcon={!loading && <ArrowIcon />}
                            sx={{
                                mt: 1,
                                mb: 2,
                                borderRadius: 1.5,
                                fontWeight: 500,
                                py: 1.2,
                                background: primaryGradient || 'primary.main',
                                '&:hover': {
                                    background: primaryGradient || 'primary.dark',
                                    filter: primaryGradient ? 'brightness(0.9)' : 'none'
                                },
                                '&.Mui-disabled': { opacity: 0.45 }
                            }}
                        >
                            {loading ? <CircularProgress size={20} color="inherit" /> : 'Ativar Conta'}
                        </Button>

                        <Divider sx={{ my: 2 }} />

                        {/* Contato */}
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                                Não recebeu um token?
                            </Typography>
                            <Button
                                size="small"
                                startIcon={<EmailIcon sx={{ fontSize: 14 }} />}
                                href={`mailto:${contactInfo.email || 'contato@mensure.com.br'}`}
                                component="a"
                                sx={{
                                    color: 'primary.main',
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    display: 'block',
                                    mb: 0.5,
                                }}
                            >
                                {contactInfo.email || 'contato@mensure.com.br'}
                            </Button>

                            {contactInfo.whatsapp && (
                                <Button
                                    size="small"
                                    startIcon={<WhatsAppIcon sx={{ fontSize: 15 }} />}
                                    href={`https://wa.me/${contactInfo.whatsapp.replace(/\D/g, '')}`}
                                    component="a"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        color: '#25D366',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        display: 'block',
                                        mb: 0.5,
                                    }}
                                >
                                    Suporte via WhatsApp
                                </Button>
                            )}

                            <Button
                                size="small"
                                startIcon={<LogoutIcon sx={{ fontSize: 14 }} />}
                                onClick={handleLogout}
                                sx={{ color: 'text.disabled', fontSize: '0.775rem', textTransform: 'none', mt: 0.5 }}
                            >
                                Sair da conta
                            </Button>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default ActivationPage;
